import type {
  MediaAsset,
  PlaylistItem,
  PlaylistRevision,
  UpdatePlaylistRevisionRequest,
} from "@led-signage/shared/management-api";

export function normalizeItems(
  items: readonly PlaylistItem[],
): PlaylistItem[] {
  return items.map((item, position) => ({ ...item, position }));
}

export function moveItem(
  items: readonly PlaylistItem[],
  from: number,
  to: number,
): PlaylistItem[] {
  if (
    from < 0 ||
    from >= items.length ||
    to < 0 ||
    to >= items.length ||
    from === to
  ) {
    return normalizeItems(items);
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return normalizeItems(items);
  next.splice(to, 0, moved);
  return normalizeItems(next);
}

export function updateItem(
  items: readonly PlaylistItem[],
  position: number,
  patch: Partial<Pick<PlaylistItem, "repeatCount" | "audioEnabled">>,
): PlaylistItem[] {
  return normalizeItems(
    items.map((item) =>
      item.position === position ? { ...item, ...patch } : item,
    ),
  );
}

export function removeItem(
  items: readonly PlaylistItem[],
  position: number,
): PlaylistItem[] {
  return normalizeItems(items.filter((item) => item.position !== position));
}

export function appendItem(
  items: readonly PlaylistItem[],
  mediaAssetId: string,
): PlaylistItem[] {
  return normalizeItems([
    ...items,
    {
      position: items.length,
      mediaAssetId,
      repeatCount: 1,
      audioEnabled: null,
    },
  ]);
}

export function revisionInput(
  defaultAudioEnabled: boolean,
  items: readonly PlaylistItem[],
): UpdatePlaylistRevisionRequest {
  return { defaultAudioEnabled, items: normalizeItems(items) };
}

export function editorDurationMs(
  items: readonly PlaylistItem[],
  media: readonly MediaAsset[],
): number | null {
  const durations = new Map(media.map((asset) => [asset.id, asset.durationMs]));
  let total = 0;
  for (const item of items) {
    const duration = durations.get(item.mediaAssetId);
    if (duration === undefined || duration === null) return null;
    total += duration * item.repeatCount;
  }
  return total;
}

export function isEditable(revision: PlaylistRevision | null): boolean {
  return revision?.state === "draft";
}
